import type { SectionKey } from "@shared/types/section_key.ts";
import type { TaxonomyGapReview } from "@shared/types/corpus/api.ts";
import {
  MESH_TREE_PREFIX_PATTERN,
  TAXONOMY_GAP_ENTRY_TYPES,
  type TaxonomyGapEntry,
  type TaxonomyJournalTier,
  type TaxonomyPartyType,
  type TaxonomyUrn,
} from "@shared/types/taxonomy.ts";

/** A section title's choice in the form: one of the section keys, or noise. */
export type SectionChoice = SectionKey | "noise";

/** What a person has typed or picked for a gap's entry, every field as the form holds it. */
export interface GapEntryDraft {
  entryType: TaxonomyGapEntry["entry"];
  tier: TaxonomyJournalTier | "";
  medlineIndexed: "" | "yes" | "no";
  partyType: TaxonomyPartyType | "";
  controlledBy: string;
  sectionChoice: SectionChoice | "";
  prefix: string;
  urns: TaxonomyUrn[];
  ignored: boolean;
}

/** The entry a draft comes to, or the one thing still wrong with it. */
export interface GapEntryReading {
  entry: TaxonomyGapEntry | null;
  problem: string | null;
}

function emptyDraft(entryType: GapEntryDraft["entryType"]): GapEntryDraft {
  return {
    entryType,
    tier: "",
    medlineIndexed: "",
    partyType: "",
    controlledBy: "",
    sectionChoice: "",
    prefix: "",
    urns: [],
    ignored: false,
  };
}

/** The form's starting point: the gap's current decision when it has one, else an empty entry of
 *  the gap's first entry type. */
export function startGapEntryDraft(gap: TaxonomyGapReview): GapEntryDraft {
  const decision = gap.decision;
  if (decision === null) return emptyDraft(TAXONOMY_GAP_ENTRY_TYPES[gap.kind][0]);

  const draft = emptyDraft(decision.entry);
  switch (decision.entry) {
    case "journal":
      return {
        ...draft,
        tier: decision.tier ?? "",
        medlineIndexed:
          decision.medline_indexed === null ? "" : decision.medline_indexed ? "yes" : "no",
      };
    case "party":
      return { ...draft, partyType: decision.type, controlledBy: decision.controlled_by ?? "" };
    case "non_party":
      return draft;
    case "section_title":
      return { ...draft, sectionChoice: decision.noise ? "noise" : (decision.section_key ?? "") };
    case "mesh_prefix":
      return { ...draft, prefix: decision.prefix, urns: decision.urns, ignored: decision.ignored };
  }
}

/** Turns a draft into the entry to decide the gap with, or says what is missing. */
export function readGapEntryDraft(draft: GapEntryDraft): GapEntryReading {
  switch (draft.entryType) {
    case "journal": {
      if (draft.tier === "" && draft.medlineIndexed === "") {
        return { entry: null, problem: "Pick a tier or say whether MEDLINE indexes the journal." };
      }
      return {
        entry: {
          entry: "journal",
          tier: draft.tier === "" ? null : draft.tier,
          medline_indexed: draft.medlineIndexed === "" ? null : draft.medlineIndexed === "yes",
        },
        problem: null,
      };
    }
    case "party": {
      if (draft.partyType === "") return { entry: null, problem: "Pick what the party is." };
      const controlledBy = draft.controlledBy.trim();
      return {
        entry: {
          entry: "party",
          type: draft.partyType,
          controlled_by: controlledBy === "" ? null : controlledBy,
        },
        problem: null,
      };
    }
    case "non_party":
      return { entry: { entry: "non_party" }, problem: null };
    case "section_title": {
      if (draft.sectionChoice === "") {
        return { entry: null, problem: "Pick a section, or mark the title as noise." };
      }
      return {
        entry: {
          entry: "section_title",
          section_key: draft.sectionChoice === "noise" ? null : draft.sectionChoice,
          noise: draft.sectionChoice === "noise",
        },
        problem: null,
      };
    }
    case "mesh_prefix": {
      const prefix = draft.prefix.trim();
      if (!MESH_TREE_PREFIX_PATTERN.test(prefix)) {
        return { entry: null, problem: "Enter a tree prefix such as C14 or C14.280." };
      }
      if (draft.urns.length === 0) return { entry: null, problem: "Pick at least one field." };
      return {
        entry: { entry: "mesh_prefix", prefix, urns: draft.urns, ignored: draft.ignored },
        problem: null,
      };
    }
  }
}

/** One line saying what an entry is, for a table cell. */
export function describeGapEntry(entry: TaxonomyGapEntry): string {
  switch (entry.entry) {
    case "journal": {
      const tier = entry.tier === null ? "no tier" : entry.tier.replace("_", " ");
      if (entry.medline_indexed === null) return `Journal, ${tier}`;
      return `Journal, ${tier}, ${entry.medline_indexed ? "" : "not "}indexed by MEDLINE`;
    }
    case "party":
      return entry.controlled_by === null
        ? `Funder, ${entry.type}`
        : `Funder, ${entry.type}, controlled by ${entry.controlled_by}`;
    case "non_party":
      return "Not a funder";
    case "section_title":
      return entry.noise ? "Noise" : `Section: ${entry.section_key}`;
    case "mesh_prefix":
      return `${entry.ignored ? "Ignored by" : "Maps to"} ${entry.urns.length} field${entry.urns.length === 1 ? "" : "s"}: ${entry.prefix}`;
  }
}
